import { Link } from "react-router";

import type { RegisteredComponent } from "@builder.io/sdk-react";

import { ModelMap } from "~/hec/builder/model-map";
import { topicUrl } from "~/hec/utils/article-urls";
import { useArticleRefTile } from "~/hooks/learning-center/use-article-pagination";
import { cn } from "~/lib/ui";

/**
 * SFCC-11498 (ARTICLES-12) — port of composable `articleTopicCard.jsx`.
 *
 * A Builder block rendering a topic navigation card from a
 * `learning-center-article-topic` reference: full image with an overlaid title
 * bar, linking to the topic-filtered listing `?topic={slug}`.
 *
 * Topic round-trip is slug-based (verified live, SFCC-11498): the link carries
 * the topic `data.id` *slug* (e.g. `hsa-and-taxes`), which the grid resource
 * route resolves back to the topic entry hash for filtering. Resolution mirrors
 * ArticleCard — server-resolved `value` first, client fallback otherwise.
 *
 * `noWrap` block — spreads `attributes` on its root element.
 */

interface TopicRefData {
  id?: string;
  title?: string;
  image?: string;
}

interface TopicReference {
  id?: string;
  value?: { data?: TopicRefData };
}

interface ArticleTopicCardProps {
  articleTopicRef?: TopicReference;
  attributes?: Record<string, unknown>;
}

const ArticleTopicCard = ({
  articleTopicRef,
  attributes,
}: ArticleTopicCardProps) => {
  const serverData = articleTopicRef?.value?.data;
  const { tile: fetchedTile } = useArticleRefTile(
    serverData ? undefined : articleTopicRef?.id,
    "topic",
  );

  const slug = serverData?.id;
  const tile =
    slug !== undefined
      ? {
          slug,
          title: serverData?.title ?? "",
          image: serverData?.image ?? "",
          url: topicUrl(slug),
        }
      : fetchedTile;

  if (!tile) return null;

  const { className, ...rest } = (attributes ?? {}) as {
    className?: string;
    [key: string]: unknown;
  };

  return (
    <Link
      to={tile.url}
      className={cn("block border-none shadow-none", className)}
      {...rest}
      data-testid="articleTopicCard"
    >
      <div className="relative overflow-hidden">
        {tile.image ? (
          <img
            src={tile.image}
            alt={tile.title}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="aspect-video w-full bg-stone-100" />
        )}
        <div className="absolute bottom-0 left-0 right-0 flex h-16 flex-col justify-start bg-primary/75 p-3 lg:h-20">
          <span
            className="text-center text-base font-medium text-white lg:text-lg"
            data-testid="articleTopicCard-Title"
          >
            {tile.title}
          </span>
        </div>
      </div>
    </Link>
  );
};

export const ArticleTopicCardDefinition = {
  component: ArticleTopicCard,
  name: "ArticleTopicCard",
  noWrap: true,
  inputs: [
    {
      name: "articleTopicRef",
      type: "reference",
      model: ModelMap.learningCenterArticleTopic,
      friendlyName: "Select Article Topic",
      required: true,
    },
  ],
} satisfies RegisteredComponent;

export default ArticleTopicCard;
